import { useEffect, useState } from 'react'
import { api, ApiError, signIn } from './api'
import { ProjectView } from './project/ProjectView'
import { Logo, Spinner } from './ui'

/*
  A signed-out visitor on /p/<id>. Three ways in:
    ?k=...         the link from an invite email (asks for a name once)
    open project   anyone with the link types their name
    invite only    enter your email, we send a sign-in link if you were invited
*/
type Gate = { access: 'invited' | 'link'; signedIn: boolean; title: string; client: string }

const savedName = () => {
  try {
    return localStorage.getItem('osel-name') || ''
  } catch {
    return ''
  }
}

export function ProjectGate({ projectId }: { projectId: string }) {
  const [gate, setGate] = useState<Gate | null>(null)
  const [entered, setEntered] = useState(false)
  const [needName, setNeedName] = useState<string | null>(null) // invite email waiting on a name
  const [name, setName] = useState(savedName)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const k = new URLSearchParams(location.search).get('k')

  const dropK = () => history.replaceState(null, '', location.pathname)
  const remember = (n: string) => {
    try {
      localStorage.setItem('osel-name', n)
    } catch {
      /* private mode */
    }
  }

  const redeem = async (withName?: string) => {
    const r = await api.post<{ ok?: boolean; needName?: boolean; email?: string }>(`p/${projectId}/redeem`, { k, name: withName })
    if (r.needName) return setNeedName(r.email || '')
    if (withName) remember(withName)
    dropK()
    setEntered(true)
  }

  useEffect(() => {
    ;(async () => {
      try {
        const g = await api.get<Gate>(`p/${projectId}/gate`)
        setGate(g)
        if (g.signedIn) return setEntered(true)
        if (k) await redeem()
      } catch (e) {
        if (k) dropK()
        setError(e instanceof ApiError ? e.message : 'This link is not working.')
      }
    })()
  }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (entered) return <ProjectView projectId={projectId} anthropic={false} />
  if (!gate) {
    return error ? <Shell><h1>Can't open this</h1><p>{error}</p></Shell> : <div className="fullpage"><Spinner /></div>
  }

  const run = (fn: () => Promise<unknown>) => async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await fn()
    } catch (x) {
      setError(x instanceof ApiError ? x.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const nameField = (
    <label className="field">
      <span>Your name</span>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="So your team knows who picked what" autoComplete="name" required maxLength={60} autoFocus />
    </label>
  )

  if (needName !== null) {
    return (
      <Shell onSubmit={run(() => redeem(name.trim()))}>
        <h1>{gate.title || 'Welcome'}</h1>
        <p>You're in as {needName}. Add your name and you're set.</p>
        {nameField}
        <button className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'Opening…' : 'Open project'}</button>
        {error && <p className="error">{error}</p>}
      </Shell>
    )
  }

  if (gate.access === 'link') {
    return (
      <Shell
        onSubmit={run(async () => {
          await api.post(`p/${projectId}/join`, { name: name.trim() })
          remember(name.trim())
          setEntered(true)
        })}
      >
        {gate.client && <p className="eyebrow">{gate.client}</p>}
        <h1>{gate.title}</h1>
        <p>Watch the interviews, read along, and mark the lines you want in the film.</p>
        {nameField}
        <button className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'Opening…' : 'Open project'}</button>
        {error && <p className="error">{error}</p>}
        <TeamLink />
      </Shell>
    )
  }

  if (sent) {
    return (
      <Shell>
        <h1>Check your email</h1>
        <p>If {sent} has access, a link to open the project is on its way from transcripts@osmo.inc.</p>
        <button className="linkish small" onClick={() => setSent('')}>Use a different email</button>
      </Shell>
    )
  }

  return (
    <Shell
      onSubmit={run(async () => {
        const r = await api.post<{ devUrl?: string }>(`p/${projectId}/send-link`, { email: email.trim() })
        if (r.devUrl) console.log('dev sign-in link', r.devUrl)
        setSent(email.trim())
      })}
    >
      <h1>You need access</h1>
      <p>{error && k ? error : 'This project is invite only. Enter the email you were invited with and we will send you a link.'}</p>
      <label className="field">
        <span>Email</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required autoFocus />
      </label>
      <button className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'Sending…' : 'Email me a link'}</button>
      {error && !k && <p className="error">{error}</p>}
      <TeamLink />
    </Shell>
  )
}

function TeamLink() {
  return <button type="button" className="linkish small muted" onClick={signIn}>Osmo team? Continue with Google</button>
}

function Shell({ children, onSubmit }: { children: React.ReactNode; onSubmit?: (e: React.FormEvent) => void }) {
  return (
    <div className="fullpage">
      <form className="signin" onSubmit={onSubmit || ((e) => e.preventDefault())}>
        <Logo size={40} />
        {children}
      </form>
    </div>
  )
}
